import { useLocation } from '@gatsbyjs/reach-router'
import React, { PropsWithChildren, useEffect } from 'react'
import Modal from 'styled-react-modal'

import useIsSsr from '../../../hooks/useIsSsr'
import { usePageUrl } from '../../../lib/pages'
import { ProductModalData, ProductModalState } from '../../../lib/products/modal/types'
import Spinner from '../../Spinner'
import * as Styled from './styled'

import closeModalIcon from '../../../img/close_modal.svg'

export interface ProductModalProps<TData extends ProductModalData> {
  state: ProductModalState<TData>
  onCloseModal: () => void
  urlHash?: string
}

export const ProductModal = <TData extends ProductModalData>({
  children,
  state,
  onCloseModal,
  urlHash,
}: PropsWithChildren<ProductModalProps<TData>>) => {
  const isSsr = useIsSsr()
  const location = useLocation()
  const { navigate } = usePageUrl()

  // update the URL hash when the modal is opened/closed
  useEffect(() => {
    if (isSsr || !urlHash) {
      return
    }

    const hash = location.hash

    const urlWithoutHash = location.href.replace(location.origin, '').replace(hash, '')

    if (state.type === 'CLOSED') {
      navigate(urlWithoutHash)
      return
    }

    if (hash !== urlHash) {
      navigate(`${urlWithoutHash}${urlHash}`)
    }
  }, [location.href, location.hash, location.origin, navigate, isSsr, state, urlHash])

  // close modal when back navigation is detected
  useEffect(() => {
    if (isSsr) {
      return
    }
    window.addEventListener('popstate', onCloseModal)
    return () => window.removeEventListener('popstate', onCloseModal)
  }, [isSsr, onCloseModal])

  if (state.type === 'CLOSED') {
    return null
  }

  return (
    <Modal isOpen onEscapeKeydown={onCloseModal} onBackgroundClick={onCloseModal}>
      <Styled.ModalContainer id="product-modal">
        {state.type === 'OPEN' && children}
        {state.type === 'LOADING' && (
          <Styled.ModalSpinnerContainer>
            <Styled.CloseButton onClick={onCloseModal} aria-label="Close modal">
              <img alt="Close modal" src={closeModalIcon} />
            </Styled.CloseButton>
            <Spinner loading />
          </Styled.ModalSpinnerContainer>
        )}
        {state.type === 'ERROR' && (
          <Styled.ModalErrorContainer>
            <div>There was an error loading the product, please try again later.</div>
          </Styled.ModalErrorContainer>
        )}
      </Styled.ModalContainer>
    </Modal>
  )
}
